import classNames from 'classnames';
import React from 'react';

interface IconProps {
  size?: number;
  colorClass?: string;
  stroke?: string;
}

interface IconComponentProps {
  name: string;
  className?: string;
  useGoogleMaterial?: boolean;
  onClick?: () => void;
  style?: React.CSSProperties;
}

interface IconPropsSvg {
  size?: number;
  'aria-label'?: string;
  color?: string;
}

function createIcon(iconName: string): React.FC<IconProps> {
  return function Icon({ size = 2, colorClass = 'text-gray-600', stroke }) {
    const px = size !== 2 ? `${size * 8}px` : undefined;
    const textStroke = size === 1 ? '1px' : stroke ? stroke : undefined;

    return (
      <span
        className={`material-symbols-outlined ${colorClass}`}
        style={{
          fontSize: px,
          width: px,
          height: px,
          WebkitTextStroke: textStroke,
        }}
      >
        {iconName}
      </span>
    );
  };
}

export const Icon: React.FC<IconComponentProps> = ({
  name,
  className,
  useGoogleMaterial,
  style,
  onClick,
}) => {
  const materialLibraryStyles = useGoogleMaterial
    ? 'material-symbols-outlined'
    : 'material-icons-outlined';

  const defaultClassNames = 'text-gray-600 text-2xl';

  const classes = className ? className : defaultClassNames;

  return (
    // eslint-disable-next-line tailwindcss/no-custom-classname
    <span
      role="button"
      tabIndex={0}
      onClick={onClick}
      className={classNames(materialLibraryStyles, classes)}
      style={style}
    >
      {name}
    </span>
  );
};

export const CloseIcon = createIcon('close');
export const KeyArrowDownIcon = createIcon('keyboard_arrow_down');
export const EditIcon = createIcon('edit');
export const PageInfoIcon = createIcon('page_info');
export const EmptyListIcon = createIcon('manage_search');

export const CIInfoIcon = ({
  size = 20,
  color = '#616161',
}: IconPropsSvg): JSX.Element => {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 21 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <mask
        id="mask0_17391_1618"
        maskUnits="userSpaceOnUse"
        x="0"
        y="0"
        width="21"
        height="20"
      >
        <rect x="0.5" width="20" height="20" fill="#D9D9D9" />
      </mask>
      <g mask="url(#mask0_17391_1618)">
        <path
          d="M9.87504 13.9583H11.125V9.16667H9.87504V13.9583ZM10.5 7.7404C10.6907 7.7404 10.8506 7.6759 10.9796 7.5469C11.1086 7.4179 11.1731 7.25804 11.1731 7.06734C11.1731 6.87664 11.1086 6.71679 10.9796 6.58777C10.8506 6.45877 10.6907 6.39427 10.5 6.39427C10.3093 6.39427 10.1495 6.45877 10.0205 6.58777C9.89146 6.71679 9.82696 6.87664 9.82696 7.06734C9.82696 7.25804 9.89146 7.4179 10.0205 7.5469C10.1495 7.6759 10.3093 7.7404 10.5 7.7404ZM10.5014 17.9166C9.40647 17.9166 8.37728 17.7089 7.41383 17.2933C6.45037 16.8778 5.6123 16.3138 4.89962 15.6014C4.18693 14.8891 3.62271 14.0514 3.20698 13.0883C2.79124 12.1253 2.58337 11.0963 2.58337 10.0014C2.58337 8.90645 2.79115 7.87726 3.20671 6.91381C3.62226 5.95036 4.18622 5.11229 4.89858 4.39961C5.61096 3.68691 6.44866 3.1227 7.41169 2.70696C8.3747 2.29122 9.40368 2.08335 10.4986 2.08335C11.5936 2.08335 12.6228 2.29113 13.5862 2.70669C14.5497 3.12224 15.3877 3.6862 16.1004 4.39856C16.8131 5.11094 17.3773 5.94864 17.7931 6.91167C18.2088 7.87468 18.4167 8.90366 18.4167 9.99861C18.4167 11.0936 18.2089 12.1227 17.7933 13.0862C17.3778 14.0496 16.8138 14.8877 16.1015 15.6004C15.3891 16.3131 14.5514 16.8773 13.5884 17.293C12.6253 17.7088 11.5964 17.9166 10.5014 17.9166ZM10.5 16.6667C12.3611 16.6667 13.9375 16.0208 15.2292 14.7292C16.5209 13.4375 17.1667 11.8611 17.1667 10C17.1667 8.13889 16.5209 6.5625 15.2292 5.27084C13.9375 3.97917 12.3611 3.33333 10.5 3.33333C8.63891 3.33333 7.06252 3.97917 5.77085 5.27084C4.47919 6.5625 3.83335 8.13889 3.83335 10C3.83335 11.8611 4.47919 13.4375 5.77085 14.7292C7.06252 16.0208 8.63891 16.6667 10.5 16.6667Z"
          fill={color}
        />
      </g>
    </svg>
  );
};

export function CICancelIcon({
  size = 20,
  color = '#E53935',
}: IconPropsSvg): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 22 23"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M7.69994 15.766L10.9999 12.466L14.2999 15.766L15.2659 14.8L11.9659 11.5L15.2659 8.20001L14.2999 7.234L10.9999 10.534L7.69994 7.234L6.73393 8.20001L10.0339 11.5L6.73393 14.8L7.69994 15.766ZM11.0015 20.2083C9.79703 20.2083 8.66492 19.9798 7.60513 19.5226C6.54533 19.0655 5.62345 18.4452 4.8395 17.6616C4.05554 16.878 3.4349 15.9565 2.97759 14.8972C2.52028 13.8379 2.29163 12.706 2.29163 11.5015C2.29163 10.2971 2.52018 9.16499 2.97729 8.1052C3.4344 7.04539 4.05476 6.12352 4.83836 5.33957C5.62197 4.55561 6.54344 3.93497 7.60277 3.47766C8.66209 3.02035 9.79396 2.79169 10.9984 2.79169C12.2028 2.79169 13.335 3.02025 14.3947 3.47736C15.4545 3.93447 16.3764 4.55483 17.1604 5.33842C17.9443 6.12204 18.565 7.04351 19.0223 8.10284C19.4796 9.16215 19.7083 10.294 19.7083 11.4985C19.7083 12.7029 19.4797 13.835 19.0226 14.8948C18.5655 15.9546 17.9451 16.8765 17.1615 17.6604C16.3779 18.4444 15.4564 19.065 14.3971 19.5224C13.3378 19.9797 12.2059 20.2083 11.0015 20.2083ZM10.9999 18.8333C13.0472 18.8333 14.7812 18.1229 16.202 16.7021C17.6229 15.2813 18.3333 13.5472 18.3333 11.5C18.3333 9.45278 17.6229 7.71876 16.202 6.29792C14.7812 4.87709 13.0472 4.16667 10.9999 4.16667C8.95271 4.16667 7.21869 4.87709 5.79785 6.29792C4.37702 7.71876 3.6666 9.45278 3.6666 11.5C3.6666 13.5472 4.37702 15.2813 5.79785 16.7021C7.21869 18.1229 8.95271 18.8333 10.9999 18.8333Z"
        fill={color}
      />
    </svg>
  );
}

export function CICheckIcon({
  size = 20,
  color = '#43A047',
}: IconPropsSvg): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 22 23"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M9.69898 15.3993L15.8618 9.23654L14.8958 8.27054L9.69898 13.4673L7.08648 10.8548L6.12047 11.8208L9.69898 15.3993ZM11.0015 20.2083C9.79703 20.2083 8.66492 19.9798 7.60513 19.5226C6.54533 19.0655 5.62345 18.4452 4.8395 17.6616C4.05554 16.878 3.4349 15.9565 2.97759 14.8972C2.52028 13.8378 2.29163 12.706 2.29163 11.5015C2.29163 10.2971 2.52018 9.16498 2.97729 8.10519C3.4344 7.04539 4.05476 6.12351 4.83836 5.33956C5.62197 4.5556 6.54344 3.93496 7.60277 3.47765C8.66209 3.02034 9.79396 2.79169 10.9984 2.79169C12.2028 2.79169 13.335 3.02024 14.3947 3.47735C15.4545 3.93447 16.3764 4.55482 17.1604 5.33842C17.9443 6.12203 18.565 7.0435 19.0223 8.10283C19.4796 9.16215 19.7083 10.294 19.7083 11.4985C19.7083 12.7029 19.4797 13.835 19.0226 14.8948C18.5655 15.9546 17.9451 16.8765 17.1615 17.6604C16.3779 18.4444 15.4564 19.065 14.3971 19.5223C13.3378 19.9797 12.2059 20.2083 11.0015 20.2083ZM10.9999 18.8333C13.0472 18.8333 14.7812 18.1229 16.202 16.7021C17.6229 15.2812 18.3333 13.5472 18.3333 11.5C18.3333 9.45278 17.6229 7.71875 16.202 6.29791C14.7812 4.87708 13.0472 4.16666 10.9999 4.16666C8.95271 4.16666 7.21869 4.87708 5.79785 6.29791C4.37702 7.71875 3.6666 9.45278 3.6666 11.5C3.6666 13.5472 4.37702 15.2812 5.79785 16.7021C7.21869 18.1229 8.95271 18.8333 10.9999 18.8333Z"
        fill={color}
      />
    </svg>
  );
}

export function CIDiamondIcon({
  size = 20,
  color = '#04709B',
}: IconPropsSvg): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 22 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <mask
        id="mask0_17391_1623"
        maskUnits="userSpaceOnUse"
        x="0"
        y="0"
        width="22"
        height="22"
      >
        <rect width="22" height="22" fill="#D9D9D9" />
      </mask>
      <g mask="url(#mask0_17391_1623)">
        <path
          d="M10.9999 18.8886L2.10645 8.21471L4.72422 2.97917H17.2757L19.8934 8.21471L10.9999 18.8886ZM8.45253 7.5625H13.5474L11.943 4.35417H10.0569L8.45253 7.5625ZM10.3124 15.92V8.9375H4.51086L10.3124 15.92ZM11.6874 15.92L17.489 8.9375H11.6874V15.92ZM15.0757 7.5625H18.0161L16.4117 4.35417H13.4713L15.0757 7.5625ZM3.98378 7.5625H6.92422L8.52861 4.35417H5.58817L3.98378 7.5625Z"
          fill={color}
        />
      </g>
    </svg>
  );
}

export function CIDownloadIcon({
  size = 20,
  color = '#757575',
}: IconPropsSvg): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 22 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M11 14.4727L7.08657 10.5593L8.05257 9.56512L10.3125 11.825V4.12502H11.6875V11.825L13.9474 9.56512L14.9134 10.5593L11 14.4727ZM5.78206 17.875C5.31902 17.875 4.92708 17.7146 4.60625 17.3937C4.28542 17.0729 4.125 16.681 4.125 16.2179V13.7324H5.49998V16.2179C5.49998 16.2884 5.52936 16.3531 5.58811 16.4119C5.64689 16.4706 5.71154 16.5 5.78206 16.5H16.2179C16.2884 16.5 16.3531 16.4706 16.4118 16.4119C16.4706 16.3531 16.5 16.2884 16.5 16.2179V13.7324H17.875V16.2179C17.875 16.681 17.7145 17.0729 17.3937 17.3937C17.0729 17.7146 16.6809 17.875 16.2179 17.875H5.78206Z"
        fill={color}
      />
    </svg>
  );
}

export function CIArrowRightIcon({
  size = 22,
  color = '#212121',
}: IconPropsSvg): JSX.Element {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 22 22"
      fill="none"
    >
      <path
        d="M12.6923 16.1827L11.7263 15.1885L15.2273 11.6875H4.12503V10.3126H15.2273L11.7263 6.8116L12.6923 5.81738L17.875 11.0001L12.6923 16.1827Z"
        fill={color}
      />
    </svg>
  );
}

export const CIExclusiveRewardsBackIcon = (): JSX.Element => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
    >
      <mask
        id="mask0_17539_73806"
        style={{ maskType: 'alpha' }}
        maskUnits="userSpaceOnUse"
        x="0"
        y="0"
        width="20"
        height="20"
      >
        <rect x="-6.10352e-05" width="20" height="20" fill="#D9D9D9" />
      </mask>
      <g mask="url(#mask0_17539_73806)">
        <path
          d="M15.8333 15.4167V12.5C15.8333 11.7521 15.5688 11.1137 15.0399 10.585C14.5112 10.0561 13.8729 9.79167 13.1249 9.79167H5.31077L8.5191 13L7.62806 13.8781L2.9166 9.16667L7.62806 4.45521L8.5191 5.33333L5.31077 8.54167H13.1249C14.219 8.54167 15.1522 8.92792 15.9245 9.70042C16.697 10.4728 17.0833 11.406 17.0833 12.5V15.4167H15.8333Z"
          fill="#B2E2ED"
        />
      </g>
    </svg>
  );
};

export const BrandIcon = (): JSX.Element => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
    >
      <path
        d="M5.25 5.25H5.2575M5.25 2.25H9C9.384 2.25 9.768 2.39625 10.0605 2.6895L15.3105 7.9395C15.5917 8.22079 15.7497 8.60225 15.7497 9C15.7497 9.39775 15.5917 9.77921 15.3105 10.0605L10.0605 15.3105C9.77921 15.5917 9.39775 15.7497 9 15.7497C8.60225 15.7497 8.22079 15.5917 7.9395 15.3105L2.6895 10.0605C2.55 9.92139 2.43937 9.75609 2.36394 9.5741C2.28852 9.39211 2.2498 9.197 2.25 9V5.25C2.25 4.45435 2.56607 3.69129 3.12868 3.12868C3.69129 2.56607 4.45435 2.25 5.25 2.25Z"
        stroke="#757575"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const CarIcon = (): JSX.Element => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
    >
      <mask
        id="mask0_17590_130821"
        style={{ maskType: 'alpha' }}
        maskUnits="userSpaceOnUse"
        x="0"
        y="0"
        width="18"
        height="18"
      >
        <rect width="18" height="18" fill="#D9D9D9" />
      </mask>
      <g mask="url(#mask0_17590_130821)">
        <path
          d="M4.21149 13.875V14.8125C4.21149 14.9718 4.15759 15.1054 4.04977 15.2132C3.94197 15.3211 3.80839 15.375 3.64901 15.375H3.1875C3.02813 15.375 2.89453 15.3211 2.78672 15.2132C2.67891 15.1054 2.625 14.9718 2.625 14.8125V9.05768L4.15674 4.6875C4.21251 4.51539 4.31491 4.37861 4.46394 4.27718C4.61298 4.17573 4.77884 4.125 4.96153 4.125H13.0673C13.2428 4.125 13.4021 4.17656 13.5453 4.27969C13.6885 4.38281 13.7878 4.51875 13.8432 4.6875L15.375 9.05768V14.8125C15.375 14.9718 15.3211 15.1054 15.2132 15.2132C15.1054 15.3211 14.9718 15.375 14.8125 15.375H14.351C14.1916 15.375 14.058 15.3211 13.9502 15.2132C13.8424 15.1054 13.7885 14.9718 13.7885 14.8125V13.875H4.21149ZM4.20574 7.93271H13.7942L12.8481 5.24998H5.1519L4.20574 7.93271ZM5.59783 11.8846C5.87073 11.8846 6.10213 11.7891 6.29203 11.598C6.48194 11.407 6.5769 11.175 6.5769 10.9021C6.5769 10.6292 6.48138 10.3978 6.29034 10.2079C6.09931 10.018 5.86734 9.92306 5.59444 9.92306C5.32154 9.92306 5.09013 10.0186 4.90022 10.2096C4.71032 10.4007 4.61537 10.6326 4.61537 10.9055C4.61537 11.1784 4.71089 11.4098 4.90193 11.5997C5.09296 11.7896 5.32493 11.8846 5.59783 11.8846ZM12.4055 11.8846C12.6784 11.8846 12.9098 11.7891 13.0997 11.598C13.2896 11.407 13.3846 11.175 13.3846 10.9021C13.3846 10.6292 13.2891 10.3978 13.098 10.2079C12.907 10.018 12.675 9.92306 12.4021 9.92306C12.1292 9.92306 11.8978 10.0186 11.7079 10.2096C11.518 10.4007 11.4231 10.6326 11.4231 10.9055C11.4231 11.1784 11.5186 11.4098 11.7096 11.5997C11.9007 11.7896 12.1326 11.8846 12.4055 11.8846ZM3.74998 12.75H14.25V9.05768H3.74998V12.75Z"
          fill="#757575"
        />
      </g>
    </svg>
  );
};

export const DateIcon = (): JSX.Element => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
    >
      <path
        d="M5.99998 10.4567C5.81633 10.4567 5.65984 10.3921 5.53052 10.2628C5.40119 10.1334 5.33653 9.97695 5.33653 9.7933C5.33653 9.60965 5.40119 9.45316 5.53052 9.32384C5.65984 9.19451 5.81633 9.12985 5.99998 9.12985C6.18363 9.12985 6.34012 9.19451 6.46944 9.32384C6.59877 9.45316 6.66343 9.60965 6.66343 9.7933C6.66343 9.97695 6.59877 10.1334 6.46944 10.2628C6.34012 10.3921 6.18363 10.4567 5.99998 10.4567ZM8.99998 10.4567C8.81633 10.4567 8.65984 10.3921 8.53052 10.2628C8.40119 10.1334 8.33653 9.97695 8.33653 9.7933C8.33653 9.60965 8.40119 9.45316 8.53052 9.32384C8.65984 9.19451 8.81633 9.12985 8.99998 9.12985C9.18363 9.12985 9.34012 9.19451 9.46944 9.32384C9.59877 9.45316 9.66343 9.60965 9.66343 9.7933C9.66343 9.97695 9.59877 10.1334 9.46944 10.2628C9.34012 10.3921 9.18363 10.4567 8.99998 10.4567ZM12 10.4567C11.8163 10.4567 11.6598 10.3921 11.5305 10.2628C11.4012 10.1334 11.3365 9.97695 11.3365 9.7933C11.3365 9.60965 11.4012 9.45316 11.5305 9.32384C11.6598 9.19451 11.8163 9.12985 12 9.12985C12.1836 9.12985 12.3401 9.19451 12.4694 9.32384C12.5988 9.45316 12.6634 9.60965 12.6634 9.7933C12.6634 9.97695 12.5988 10.1334 12.4694 10.2628C12.3401 10.3921 12.1836 10.4567 12 10.4567ZM3.98077 16.125C3.60193 16.125 3.28125 15.9938 3.01875 15.7313C2.75625 15.4688 2.625 15.1481 2.625 14.7692V4.73082C2.625 4.35197 2.75625 4.03129 3.01875 3.76879C3.28125 3.50629 3.60193 3.37504 3.98077 3.37504H5.01924V1.78851H6.17306V3.37504H11.8557V1.78851H12.9807V3.37504H14.0192C14.398 3.37504 14.7187 3.50629 14.9812 3.76879C15.2437 4.03129 15.375 4.35197 15.375 4.73082V14.7692C15.375 15.1481 15.2437 15.4688 14.9812 15.7313C14.7187 15.9938 14.398 16.125 14.0192 16.125H3.98077ZM3.98077 15H14.0192C14.0769 15 14.1298 14.976 14.1779 14.9279C14.2259 14.8798 14.25 14.8269 14.25 14.7692V7.73082H3.74998V14.7692C3.74998 14.8269 3.77402 14.8798 3.82209 14.9279C3.87018 14.976 3.92308 15 3.98077 15ZM3.74998 6.60584H14.25V4.73082C14.25 4.67312 14.2259 4.62023 14.1779 4.57214C14.1298 4.52406 14.0769 4.50003 14.0192 4.50003H3.98077C3.92308 4.50003 3.87018 4.52406 3.82209 4.57214C3.77402 4.62023 3.74998 4.67312 3.74998 4.73082V6.60584Z"
        fill="#757575"
      />
    </svg>
  );
};

export const GenderIcon = (): JSX.Element => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
    >
      <mask
        id="mask0_17590_130832"
        style={{ maskType: 'alpha' }}
        maskUnits="userSpaceOnUse"
        x="0"
        y="0"
        width="18"
        height="18"
      >
        <rect width="18" height="18" fill="#D9D9D9" />
      </mask>
      <g mask="url(#mask0_17590_130832)">
        <path
          d="M1.34863 14.4808V12.8135C1.34863 12.4269 1.4484 12.082 1.64792 11.7786C1.84743 11.4753 2.11388 11.2393 2.44727 11.0706C3.16003 10.7219 3.87675 10.4532 4.59741 10.2642C5.31808 10.0753 6.11015 9.98082 6.97361 9.98082C7.83708 9.98082 8.62914 10.0753 9.3498 10.2642C10.0705 10.4532 10.7872 10.7219 11.5 11.0706C11.8333 11.2393 12.0998 11.4753 12.2993 11.7786C12.4988 12.082 12.5986 12.4269 12.5986 12.8135V14.4808H1.34863ZM14.0986 14.4808V12.7212C14.0986 12.2289 13.978 11.7597 13.737 11.3136C13.4959 10.8676 13.1539 10.4848 12.7111 10.1654C13.214 10.2404 13.6914 10.3565 14.1433 10.5138C14.5952 10.671 15.0265 10.8568 15.437 11.0712C15.8245 11.2779 16.1238 11.5216 16.3349 11.8021C16.5459 12.0827 16.6514 12.3891 16.6514 12.7212V14.4808H14.0986ZM6.97361 8.76923C6.25175 8.76923 5.63379 8.51221 5.11973 7.99816C4.60566 7.48409 4.34863 6.86613 4.34863 6.14427C4.34863 5.42239 4.60566 4.80443 5.11973 4.29038C5.63379 3.77632 6.25175 3.51929 6.97361 3.51929C7.69548 3.51929 8.31344 3.77632 8.8275 4.29038C9.34155 4.80443 9.59858 5.42239 9.59858 6.14427C9.59858 6.86613 9.34155 7.48409 8.8275 7.99816C8.31344 8.51221 7.69548 8.76923 6.97361 8.76923ZM13.4495 6.14427C13.4495 6.86613 13.1925 7.48409 12.6784 7.99816C12.1644 8.51221 11.5464 8.76923 10.8245 8.76923C10.7399 8.76923 10.6322 8.75962 10.5015 8.74039C10.3707 8.72117 10.263 8.70002 10.1784 8.67694C10.4742 8.32136 10.7015 7.92688 10.8603 7.4935C11.0192 7.06013 11.0986 6.61008 11.0986 6.14337C11.0986 5.67667 11.0176 5.22841 10.8556 4.7986C10.6935 4.3688 10.4678 3.97313 10.1784 3.61159C10.2861 3.57313 10.3938 3.54813 10.5015 3.53659C10.6092 3.52506 10.7168 3.51929 10.8245 3.51929C11.5464 3.51929 12.1644 3.77632 12.6784 4.29038C13.1925 4.80443 13.4495 5.42239 13.4495 6.14427ZM2.47361 13.3558H11.4736V12.8135C11.4736 12.6568 11.4344 12.5173 11.3561 12.3952C11.2777 12.2731 11.1534 12.1664 10.9832 12.075C10.3659 11.7567 9.73034 11.5156 9.0765 11.3517C8.42265 11.1878 7.72169 11.1058 6.97361 11.1058C6.22554 11.1058 5.52458 11.1878 4.87073 11.3517C4.21689 11.5156 3.58131 11.7567 2.964 12.075C2.79381 12.1664 2.66953 12.2731 2.59116 12.3952C2.5128 12.5173 2.47361 12.6568 2.47361 12.8135V13.3558ZM6.97361 7.64427C7.38611 7.64427 7.73924 7.49739 8.03299 7.20364C8.32674 6.90989 8.47361 6.55677 8.47361 6.14427C8.47361 5.73177 8.32674 5.37864 8.03299 5.08489C7.73924 4.79114 7.38611 4.64427 6.97361 4.64427C6.56111 4.64427 6.20799 4.79114 5.91424 5.08489C5.62049 5.37864 5.47361 5.73177 5.47361 6.14427C5.47361 6.55677 5.62049 6.90989 5.91424 7.20364C6.20799 7.49739 6.56111 7.64427 6.97361 7.64427Z"
          fill="#757575"
        />
      </g>
    </svg>
  );
};

export const MaritalStatusIcon = (): JSX.Element => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
    >
      <path
        d="M9.00001 6.70669C8.35189 6.70669 7.80045 6.47932 7.3457 6.02457C6.89095 5.56969 6.66357 5.01826 6.66357 4.37026C6.66357 3.72213 6.89095 3.17069 7.3457 2.71594C7.80045 2.26107 8.35189 2.03363 9.00001 2.03363C9.64814 2.03363 10.1996 2.26107 10.6543 2.71594C11.1091 3.17069 11.3364 3.72213 11.3364 4.37026C11.3364 5.01826 11.1091 5.56969 10.6543 6.02457C10.1996 6.47932 9.64814 6.70669 9.00001 6.70669ZM9.00001 5.58169C9.33651 5.58169 9.62257 5.46394 9.8582 5.22844C10.0938 4.99282 10.2116 4.70676 10.2116 4.37026C10.2116 4.03363 10.0938 3.74757 9.8582 3.51207C9.62257 3.27644 9.33651 3.15863 9.00001 3.15863C8.66351 3.15863 8.37745 3.27644 8.14182 3.51207C7.9062 3.74757 7.78839 4.03363 7.78839 4.37026C7.78839 4.70676 7.9062 4.99282 8.14182 5.22844C8.37745 5.46394 8.66351 5.58169 9.00001 5.58169ZM9.00001 15.8509L4.70195 11.5528C4.48082 11.3317 4.31495 11.0805 4.20432 10.7993C4.09382 10.518 4.03857 10.2107 4.03857 9.87732C4.03857 9.22157 4.26382 8.66863 4.71432 8.21851C5.16495 7.76838 5.71851 7.54332 6.37501 7.54332C6.69639 7.54332 6.99282 7.60363 7.26432 7.72426C7.53582 7.84501 7.78457 8.01832 8.01057 8.24419L9.00001 9.23363L9.98945 8.24419C10.2106 8.02307 10.4586 7.85094 10.7336 7.72782C11.0086 7.60482 11.3068 7.54332 11.6282 7.54332C12.2818 7.54332 12.8341 7.76838 13.2851 8.21851C13.736 8.66863 13.9614 9.22157 13.9614 9.87732C13.9614 10.2107 13.9062 10.518 13.7957 10.7993C13.6851 11.0805 13.5192 11.3317 13.2981 11.5528L9.00001 15.8509ZM9.00001 14.2472L12.4847 10.7625C12.6068 10.6404 12.696 10.5038 12.7523 10.3528C12.8085 10.2019 12.8366 10.0454 12.8366 9.88332C12.8366 9.55932 12.7208 9.27582 12.489 9.03282C12.2573 8.78982 11.969 8.66832 11.6243 8.66832C11.4651 8.66832 11.3213 8.69226 11.1928 8.74013C11.0642 8.78813 10.939 8.87375 10.8173 8.997L9.00001 10.8144L7.1972 9.01144C7.11407 8.92569 7.00676 8.84707 6.87526 8.77557C6.74364 8.70407 6.57795 8.66832 6.3782 8.66832C6.03182 8.66832 5.74276 8.78919 5.51101 9.03094C5.27926 9.27282 5.16339 9.55494 5.16339 9.87732C5.16339 10.0386 5.19345 10.1921 5.25357 10.3378C5.3137 10.4834 5.40095 10.6173 5.51532 10.7394L9.00001 14.2472Z"
        fill="#757575"
      />
    </svg>
  );
};

export const AgeIcon = (): JSX.Element => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
    >
      <mask
        id="mask0_17590_130843"
        style={{ maskType: 'alpha' }}
        maskUnits="userSpaceOnUse"
        x="0"
        y="0"
        width="18"
        height="18"
      >
        <rect width="18" height="18" fill="#D9D9D9" />
      </mask>
      <g mask="url(#mask0_17590_130843)">
        <path
          d="M6.74998 10.6875C6.48748 10.6875 6.26561 10.5969 6.08436 10.4156C5.90311 10.2344 5.81248 10.0125 5.81248 9.74998C5.81248 9.48748 5.90311 9.26561 6.08436 9.08436C6.26561 8.90311 6.48748 8.81248 6.74998 8.81248C7.01248 8.81248 7.23436 8.90311 7.41561 9.08436C7.59686 9.26561 7.68748 9.48748 7.68748 9.74998C7.68748 10.0125 7.59686 10.2344 7.41561 10.4156C7.23436 10.5969 7.01248 10.6875 6.74998 10.6875ZM11.25 10.6875C10.9875 10.6875 10.7656 10.5969 10.5844 10.4156C10.4031 10.2344 10.3125 10.0125 10.3125 9.74998C10.3125 9.48748 10.4031 9.26561 10.5844 9.08436C10.7656 8.90311 10.9875 8.81248 11.25 8.81248C11.5125 8.81248 11.7344 8.90311 11.9156 9.08436C12.0969 9.26561 12.1875 9.48748 12.1875 9.74998C12.1875 10.0125 12.0969 10.2344 11.9156 10.4156C11.7344 10.5969 11.5125 10.6875 11.25 10.6875ZM8.99998 15C10.675 15 12.0937 14.4187 13.2562 13.2562C14.4187 12.0937 15 10.675 15 8.99998C15 8.69998 14.9812 8.40936 14.9437 8.12811C14.9062 7.84686 14.8375 7.57498 14.7375 7.31248C14.475 7.37498 14.2125 7.42186 13.95 7.45311C13.6875 7.48436 13.4125 7.49998 13.125 7.49998C11.9875 7.49998 10.9125 7.25623 9.89998 6.76873C8.88748 6.28123 8.02498 5.59998 7.31248 4.72498C6.91248 5.69998 6.34061 6.54686 5.59686 7.26561C4.85311 7.98436 3.98748 8.52498 2.99998 8.88748V8.99998C2.99998 10.675 3.58123 12.0937 4.74373 13.2562C5.90623 14.4187 7.32498 15 8.99998 15ZM9.00124 16.125C8.01579 16.125 7.08951 15.938 6.22241 15.564C5.3553 15.19 4.60104 14.6824 3.95963 14.0413C3.3182 13.4001 2.81041 12.6462 2.43624 11.7795C2.06208 10.9128 1.875 9.98669 1.875 9.00124C1.875 8.01579 2.062 7.08951 2.436 6.22241C2.81 5.3553 3.31756 4.60104 3.95869 3.95963C4.59983 3.3182 5.35376 2.81041 6.22048 2.43624C7.08719 2.06208 8.01328 1.875 8.99873 1.875C9.98418 1.875 10.9105 2.062 11.7776 2.436C12.6447 2.81 13.3989 3.31756 14.0403 3.95869C14.6818 4.59983 15.1896 5.35376 15.5637 6.22048C15.9379 7.08719 16.125 8.01328 16.125 8.99873C16.125 9.98418 15.938 10.9104 15.564 11.7775C15.19 12.6447 14.6824 13.3989 14.0413 14.0403C13.4001 14.6818 12.6462 15.1896 11.7795 15.5637C10.9128 15.9379 9.98669 16.125 9.00124 16.125ZM7.63408 3.16584C8.20236 4.21394 8.99059 5.01083 9.99877 5.55651C11.0069 6.10217 12.049 6.375 13.125 6.375C13.3288 6.375 13.5276 6.36443 13.7214 6.34328C13.9151 6.32213 14.1115 6.29184 14.3106 6.25241C13.8096 5.25721 13.0839 4.46634 12.1334 3.87979C11.1829 3.29325 10.1384 2.99998 8.99998 2.99998C8.74806 2.99998 8.51319 3.01416 8.29539 3.04253C8.07761 3.0709 7.85717 3.11201 7.63408 3.16584ZM3.21054 7.5678C3.91536 7.21491 4.5846 6.69929 5.21827 6.02093C5.85194 5.34255 6.26108 4.52931 6.44569 3.58121C5.62547 3.95334 4.93219 4.4882 4.36584 5.1858C3.79949 5.8834 3.41439 6.6774 3.21054 7.5678Z"
          fill="#757575"
        />
      </g>
    </svg>
  );
};

export const DrivingIcon = (): JSX.Element => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
    >
      <path
        d="M1.875 9.80775L3.40669 5.4375C3.46256 5.26537 3.565 5.12863 3.714 5.02725C3.863 4.92575 4.02887 4.875 4.21163 4.875H7.62113C7.59812 5.0625 7.589 5.25 7.59375 5.4375C7.59863 5.625 7.615 5.8125 7.64287 6H4.40194L3.45581 8.68275H9.13425C9.39 8.93462 9.66713 9.15625 9.96563 9.34762C10.2642 9.539 10.5905 9.69237 10.9444 9.80775H3V13.5H13.5V10.2347C13.6904 10.2116 13.8817 10.1709 14.0739 10.1128C14.2663 10.0546 14.45 9.98119 14.625 9.89269V15.5625C14.625 15.7219 14.5711 15.8554 14.4632 15.9632C14.3554 16.0711 14.2219 16.125 14.0625 16.125H13.6011C13.4417 16.125 13.3081 16.0711 13.2002 15.9632C13.0924 15.8554 13.0386 15.7219 13.0386 15.5625V14.625H3.46144V15.5625C3.46144 15.7219 3.40756 15.8554 3.29981 15.9632C3.19194 16.0711 3.05831 16.125 2.89894 16.125H2.4375C2.27812 16.125 2.14456 16.0711 2.03681 15.9632C1.92894 15.8554 1.875 15.7219 1.875 15.5625V9.80775ZM11.6556 12.6347C11.9284 12.6347 12.1598 12.5391 12.3497 12.348C12.5397 12.157 12.6347 11.9251 12.6347 11.6522C12.6347 11.3793 12.5391 11.1479 12.348 10.9579C12.157 10.768 11.9251 10.6731 11.6522 10.6731C11.3793 10.6731 11.1479 10.7686 10.9579 10.9596C10.768 11.1507 10.6731 11.3827 10.6731 11.6556C10.6731 11.9284 10.7686 12.1598 10.9596 12.3497C11.1507 12.5397 11.3827 12.6347 11.6556 12.6347ZM4.84781 12.6347C5.12069 12.6347 5.35212 12.5391 5.54212 12.348C5.732 12.157 5.82694 11.9251 5.82694 11.6522C5.82694 11.3793 5.73144 11.1479 5.54044 10.9579C5.34931 10.768 5.11731 10.6731 4.84444 10.6731C4.57156 10.6731 4.34019 10.7686 4.15031 10.9596C3.96031 11.1507 3.86531 11.3827 3.86531 11.6556C3.86531 11.9284 3.96088 12.1598 4.152 12.3497C4.343 12.5397 4.57494 12.6347 4.84781 12.6347Z"
        fill="#757575"
      />
      <path
        d="M13.6125 6.97781L14.1028 6.4875L12.7067 5.11725V3.04331H12.0431V5.38556L13.6125 6.97781ZM12.372 8.625C11.4374 8.625 10.6417 8.29663 9.98512 7.63988C9.32837 6.98313 9 6.18562 9 5.24737C9 4.32025 9.3265 3.52644 9.9795 2.86594C10.6326 2.20531 11.4321 1.875 12.3778 1.875C13.3124 1.875 14.1081 2.20337 14.7649 2.86012C15.4216 3.51687 15.75 4.3135 15.75 5.25C15.75 6.1865 15.4206 6.98313 14.7619 7.63988C14.1031 8.29663 13.3065 8.625 12.372 8.625Z"
        fill="#757575"
      />
    </svg>
  );
};

export const ListIcon = (): JSX.Element => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
    >
      <mask
        id="mask0_17590_130860"
        style={{ maskType: 'alpha' }}
        maskUnits="userSpaceOnUse"
        x="0"
        y="0"
        width="18"
        height="18"
      >
        <rect width="18" height="18" fill="#D9D9D9" />
      </mask>
      <g mask="url(#mask0_17590_130860)">
        <path
          d="M5.4375 12.5625H10.3125V11.4375H5.4375V12.5625ZM5.4375 9.56246H12.5625V8.4375H5.4375V9.56246ZM5.4375 6.56246H12.5625V5.4375H5.4375V6.56246ZM3.98078 15.375C3.60193 15.375 3.28125 15.2437 3.01875 14.9812C2.75625 14.7187 2.625 14.398 2.625 14.0192V3.98078C2.625 3.60193 2.75625 3.28125 3.01875 3.01875C3.28125 2.75625 3.60193 2.625 3.98078 2.625H14.0192C14.398 2.625 14.7187 2.75625 14.9812 3.01875C15.2437 3.28125 15.375 3.60193 15.375 3.98078V14.0192C15.375 14.398 15.2437 14.7187 14.9812 14.9812C14.7187 15.2437 14.398 15.375 14.0192 15.375H3.98078ZM3.98078 14.25H14.0192C14.0769 14.25 14.1298 14.2259 14.1779 14.1779C14.2259 14.1298 14.25 14.0769 14.25 14.0192V3.98078C14.25 3.92308 14.2259 3.87018 14.1779 3.82209C14.1298 3.77402 14.0769 3.74998 14.0192 3.74998H3.98078C3.92308 3.74998 3.87018 3.77402 3.82209 3.82209C3.77402 3.87018 3.74998 3.92308 3.74998 3.98078V14.0192C3.74998 14.0769 3.77402 14.1298 3.82209 14.1779C3.87018 14.2259 3.92308 14.25 3.98078 14.25Z"
          fill="#757575"
        />
      </g>
    </svg>
  );
};

export const SuccessIcon: React.FC = () => {
  return (
    <svg
      width="128"
      height="128"
      viewBox="0 0 128 128"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="128" height="128" rx="64" fill="#DAEAC7" />
      <mask
        id="mask0_16191_13212"
        style={{ maskType: 'alpha' }}
        maskUnits="userSpaceOnUse"
        x="16"
        y="16"
        width="96"
        height="96"
      >
        <rect x="16" y="16" width="96" height="96" fill="#D9D9D9" />
      </mask>
      <g mask="url(#mask0_16191_13212)">
        <path
          d="M52.2148 100.923L45.5378 89.538L32.5148 86.9L33.8308 73.815L25.3848 64L33.8308 54.208L32.5148 41.2L45.5378 38.485L52.2148 27.077L63.9998 32.277L75.7848 27.077L82.5618 38.485L95.5618 41.2L94.1688 54.208L102.615 64L94.1688 73.815L95.5618 86.9L82.5618 89.538L75.7848 100.923L63.9998 95.723L52.2148 100.923ZM53.6078 96.869L63.9998 92.6L74.5378 96.869L80.7768 87.1L92.0918 84.408L91.0458 72.738L98.7618 64L91.0458 55.215L92.0918 43.469L80.7768 40.9L74.4918 31.131L63.9998 35.4L53.4618 31.131L47.2228 40.9L35.9078 43.469L36.9538 55.215L29.2378 64L36.9538 72.738L35.9078 84.454L47.2228 87.1L53.6078 96.869ZM59.7768 74.992L79.6308 55.262L77.2848 53.162L59.7768 70.623L50.7378 61.338L48.3688 63.685L59.7768 74.992Z"
          fill="#3D6A01"
        />
      </g>
    </svg>
  );
};

export const MessageIcon = ({
  size = 64,
  color = '#004A68',
}: IconPropsSvg): JSX.Element => {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 99 101"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <mask
        id="mask0_17146_9039"
        maskUnits="userSpaceOnUse"
        x="0"
        y="0"
        width="96"
        height="97"
      >
        <rect y="0.00170898" width="96" height="96" fill="#D9D9D9" />
      </mask>
      <g mask="url(#mask0_17146_9039)">
        <path
          d="M47.8769 43.6846L79.3539 23.0769H16.4769L47.8769 43.6846ZM47.8769 47.2231L15.0769 25.5231V69.8769C15.0769 70.5949 15.3077 71.1847 15.7692 71.6462C16.2307 72.1077 16.8205 72.3385 17.5384 72.3385H38.9846L42.0616 75.4154H17.5384C16.0013 75.4154 14.6939 74.8766 13.6163 73.7991C12.5388 72.7215 12 71.4141 12 69.8769V25.5384C12 24.0013 12.5388 22.6939 13.6163 21.6163C14.6939 20.5388 16.0013 20 17.5384 20H78.3154C79.8526 20 81.16 20.5388 82.2375 21.6163C83.3151 22.6939 83.8539 24.0013 83.8539 25.5384V48.4769L80.7769 51.5539V25.5231L47.8769 47.2231Z"
          fill="#004A68"
        />
      </g>
      <path
        d="M74.0313 87.1388L71.5021 84.5358L80.6683 75.3696H51.6006V71.7697H80.6683L71.5021 62.6035L74.0313 60.0005L87.6005 73.5697L74.0313 87.1388Z"
        fill={color}
      />
    </svg>
  );
};

export const CategoryIcon = ({
  size = 18,
  color = '#616161',
}: IconPropsSvg): JSX.Element => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      height={size}
      viewBox="0 -960 960 960"
      width={size}
      fill={color}
    >
      <path d="m260-520 220-360 220 360H260ZM700-80q-75 0-127.5-52.5T520-260q0-75 52.5-127.5T700-440q75 0 127.5 52.5T880-260q0 75-52.5 127.5T700-80Zm-580-20v-320h320v320H120Zm580-60q42 0 71-29t29-71q0-42-29-71t-71-29q-42 0-71 29t-29 71q0 42 29 71t71 29Zm-500-20h160v-160H200v160Zm202-420h156l-78-126-78 126Zm78 0ZM360-340Zm340 80Z" />
    </svg>
  );
};
